import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LocaleProvider } from "./contexts/LocaleContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";

// Only Home stays eager (LCP). Everything else code-splits so the initial
// bundle only carries the landing page; other routes are fetched on demand.
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Ideas = lazy(() => import("./pages/Ideas"));
const IdeaDetail = lazy(() => import("./pages/IdeaDetail"));
const SobreElLibro = lazy(() => import("./pages/SobreElLibro"));
const SobreEdmundo = lazy(() => import("./pages/SobreEdmundo"));
const Admin = lazy(() => import("./pages/Admin"));

function PageFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-gold" />
    </div>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/en" component={Home} />
      <Route path="/blog">
        <Lazy><Blog /></Lazy>
      </Route>
      <Route path="/blog/:slug">
        {(params) => (
          <Lazy>
            <BlogPost key={params.slug} />
          </Lazy>
        )}
      </Route>
      <Route path="/ideas">
        <Lazy><Ideas /></Lazy>
      </Route>
      <Route path="/ideas/:slug">
        {(params) => (
          <Lazy>
            <IdeaDetail key={params.slug} />
          </Lazy>
        )}
      </Route>
      <Route path="/sobre-el-libro">
        <Lazy><SobreElLibro /></Lazy>
      </Route>
      <Route path="/about-the-book">
        <Lazy><SobreElLibro /></Lazy>
      </Route>
      <Route path="/edmundo-trevino">
        <Lazy><SobreEdmundo /></Lazy>
      </Route>
      <Route path="/about-edmundo">
        <Lazy><SobreEdmundo /></Lazy>
      </Route>
      <Route path="/admin">
        <Lazy><Admin /></Lazy>
      </Route>
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <LocaleProvider>
          <TooltipProvider>
            <Toaster />
            <div className="flex flex-col min-h-screen bg-background text-foreground">
              <Navbar />
              <main className="flex-1">
                <Router />
              </main>
              <Footer />
            </div>
          </TooltipProvider>
        </LocaleProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
